import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import ProductGalleryContainer from 'Scripts/Pages/ProductDetailPage/ProductGalleryContainer';
import { IRootState } from 'Scripts/store';

import { dpr } from 'Utils/dpr';
import tailwindBreakpoints from 'Utils/tailwindBreakpoints';

import { IImageBaseViewModel } from 'Types/Litium';
import Accordion from 'Pages/ProductDetailPage/Accordion';
import ProductImageContainer from 'Pages/ProductDetailPage/ProductImageContainer';
import ProductInfo from 'Pages/ProductDetailPage/ProductInfo';
import BreadCrumbs from 'Components/BreadCrumbs';
import DividerWide from 'Components/DividerWide';
import Flowbox from 'Components/Flowbox';
import ProductSlider from 'Components/ProductSlider/ProductSlider';

interface ISlideModel extends IImageBaseViewModel {
    videoUrl?: string;
    id: string;
}

const ProductDetailPage = () => {
    const {
        productItem,
        colorText,
        sizeText,
        breadCrumbs,
        whatsMySizePage,
        productData,
        translations,
        pageName,
        flowboxViewModel,
        productWebText,
        productWebRichText,
        collectionInfo,
        shopWithStyleProducts,
        combinationsProducts,
        productFilters,
        valueText,
    } = useSelector((state: IRootState) => state?.productDetailPage);

    const [preSelected, setPreSelected] = useState(false);

    useEffect(() => {
        const isBrowser = typeof window !== 'undefined';
        if (!isBrowser) {
            return;
        }
        if (document.URL.toLowerCase().includes('selectedsize')) {
            setPreSelected(true);
        }
        document.title = pageName;
        window.history.replaceState({}, '', productItem?.url);
    }, [productItem?.url, pageName]);

    const slides = useMemo(() => {
        const initialSlides: ISlideModel[] = productItem.images.map(x => ({
            ...x,
            id: x.filename,
        }));
        if (productItem.videoUrl && initialSlides.length > 0) {
            const firstSlide = initialSlides.shift();
            initialSlides.unshift({
                ...firstSlide,
                videoUrl: productItem.videoUrl,
                id: `${firstSlide.id}video`,
            });
            initialSlides.unshift(firstSlide);
        }
        return initialSlides;
    }, [productItem]);

    return (
        <>
            {breadCrumbs?.contentLinks && (
                <BreadCrumbs contentLinks={breadCrumbs.contentLinks} />
            )}
            <div className="flex h-full w-full flex-col flex-wrap bg-merino">
                <div
                    style={
                        {
                            '--padding-x-mobile': '14px',
                        } as React.CSSProperties
                    }
                    className="mx-auto grid w-full max-w-content px-[var(--padding-x-mobile)] lg:gap-8 lg:px-14 lg:pb-4"
                >
                    <div className="grid h-full w-full gap-6 lg:grid-cols-2">
                        <div className="relative @container">
                            <div className="hidden lg:contents">
                                <ProductGalleryContainer
                                    slides={slides}
                                    thumbnails={productItem.variantThumbnails}
                                    translations={translations}
                                    videoUrl={productItem.videoUrl}
                                />
                            </div>
                            <div className="contents lg:hidden">
                                <ProductImageContainer
                                    slides={slides}
                                    thumbnails={productItem.variantThumbnails}
                                    translations={translations}
                                    videoUrl={productItem.videoUrl}
                                />
                            </div>
                            {productItem.productTag?.src && (
                                <picture>
                                    <source
                                        media={`(min-width: ${tailwindBreakpoints.xxl})`}
                                        srcSet={dpr(
                                            `${productItem.productTag?.src}?format=webp&width=142`
                                        )}
                                    />
                                    <source
                                        media={`(min-width: ${tailwindBreakpoints.xl})`}
                                        srcSet={dpr(
                                            `${productItem.productTag?.src}?format=webp&width=97`
                                        )}
                                    />
                                    <source
                                        media={`(min-width: ${tailwindBreakpoints.lg})`}
                                        srcSet={dpr(
                                            `${productItem.productTag?.src}?format=webp&width=66`
                                        )}
                                    />
                                    <source
                                        media={`(min-width: ${tailwindBreakpoints.md})`}
                                        srcSet={dpr(
                                            `${productItem.productTag?.src}?format=webp&width=111`
                                        )}
                                    />
                                    <img
                                        className="absolute left-3 top-3 z-[2] w-8 supports-[width:8cqw]:w-[clamp(32px,_8cqw,_8cqw)] lg:w-14 lg:supports-[width:8cqw]:w-[clamp(32px,_8cqw,_8cqw)]"
                                        src={`${productItem.productTag?.src}?format=webp&width=51`}
                                        srcSet={dpr(
                                            `${productItem.productTag?.src}?format=webp&width=51`
                                        )}
                                        alt={productItem.productTag?.altText}
                                        width={
                                            productItem.productTag?.width
                                                ? productItem.productTag?.width
                                                : ''
                                        }
                                        height={
                                            productItem.productTag?.height
                                                ? productItem.productTag?.height
                                                : ''
                                        }
                                    />
                                </picture>
                            )}
                        </div>
                        <aside
                            style={
                                {
                                    '--pdp-aside-padding-x-mobile': '18px',
                                } as React.CSSProperties
                            }
                            className="flex h-[fit-content] flex-col gap-8 bg-white px-[var(--pdp-aside-padding-x-mobile)]  pb-0 pt-4 lg:px-12 lg:pt-8"
                        >
                            <ProductInfo
                                productItem={productItem}
                                colorText={colorText}
                                sizeText={sizeText}
                                whatsMySizePage={whatsMySizePage}
                                translations={translations}
                                productWebText={productWebText}
                                productWebRichText={productWebRichText}
                                showQuantityField={
                                    productItem.showQuantityField
                                }
                                productFilters={productFilters}
                                valueText={valueText}
                                isOnWishList={productItem.isOnWishList}
                                enabledWishlist={productItem.enabledWishlist}
                                preSelected={preSelected}
                                wishListIconText={productItem.wishListIconText}
                            />
                            <div className="accordion-info">
                                <Accordion productData={productData} />
                            </div>
                        </aside>
                    </div>
                </div>
                {combinationsProducts.collection.value?.length > 0 && (
                    <>
                        <DividerWide margin="my-10" />
                        <div className="mx-auto flex w-full max-w-[1380px]">
                            <ProductSlider
                                showSizes
                                title={combinationsProducts.title}
                                products={combinationsProducts.collection.value}
                                itemsToShowDesktop={3}
                                itemsToShowMobile={2}
                                marginBetweenSlidesDesktop={10}
                                marginBetweenSlidesMobile={3}
                                translations={{
                                    nextSlide: translations.generalNextSlide,
                                    previousSlide:
                                        translations.generalPreviousSlide,
                                }}
                                showArrows
                                autoSlide={false}
                                textWidth={100}
                                autoSlideTime={5}
                            />
                        </div>
                    </>
                )}

                {shopWithStyleProducts?.collection?.value?.length > 0 ? (
                    <>
                        <DividerWide margin="my-10" />
                        <div className="mx-auto flex w-full max-w-[1380px]">
                            <ProductSlider
                                showSizes
                                title={
                                    collectionInfo?.collectionInfoTitle
                                        ? collectionInfo?.collectionInfoTitle
                                        : shopWithStyleProducts.title
                                }
                                products={
                                    shopWithStyleProducts.collection.value
                                }
                                translations={{
                                    nextSlide: translations.generalNextSlide,
                                    previousSlide:
                                        translations.generalPreviousSlide,
                                }}
                                itemsToShowDesktop={4}
                                itemsToShowMobile={2}
                                marginBetweenSlidesDesktop={10}
                                marginBetweenSlidesMobile={3}
                                showArrows
                                autoSlide={false}
                                textWidth={100}
                                autoSlideTime={5}
                            />
                        </div>
                        {collectionInfo?.collectionInfoContent && (
                            <div className="mx-auto mb-1 mt-4 px-4 text-center lg:mb-0 lg:mt-7 lg:w-1/2 lg:px-0 lg:text-left xl:w-2/5">
                                <p className="font-roboto text-sm text-black">
                                    {collectionInfo.collectionInfoContent}
                                </p>
                            </div>
                        )}
                    </>
                ) : (
                    collectionInfo?.collectionInfoTitle && (
                        <>
                            <DividerWide margin="my-10" />
                            <section className="mx-auto flex w-full max-w-[1380px] flex-col flex-wrap content-center justify-center gap-10 px-3.5 lg:flex-row lg:flex-nowrap lg:items-center lg:px-14">
                                <div className="order-1 text-center lg:-order-1 lg:w-1/2 lg:text-left xl:w-2/5">
                                    <h4 className="mb-5 font-montserrat text-2xl text-black">
                                        {collectionInfo.collectionInfoTitle}
                                    </h4>
                                    <p className="font-roboto text-base text-black">
                                        {collectionInfo.collectionInfoContent}
                                    </p>
                                </div>
                                {collectionInfo.collectionInfoImage?.src && (
                                    <picture className="flex self-center lg:w-1/2 xl:w-1/3">
                                        <source
                                            media={`(min-width: ${tailwindBreakpoints.lg})`}
                                            srcSet={dpr(
                                                `${collectionInfo.collectionInfoImage.src}?format=webp&width=423&height=229&fit=crop`
                                            )}
                                            width={423}
                                            height={229}
                                        />
                                        <source
                                            media={`(min-width: ${tailwindBreakpoints.md})`}
                                            width={846}
                                            height={459}
                                            srcSet={dpr(
                                                `${collectionInfo.collectionInfoImage.src}?format=webp&width=846&height=459&fit=crop`
                                            )}
                                        />
                                        <img
                                            width={423}
                                            height={229}
                                            className="h-auto w-auto object-cover"
                                            src={`${collectionInfo.collectionInfoImage.src}?format=webp&width=423&height=229&fit=crop`}
                                            srcSet={dpr(
                                                `${collectionInfo.collectionInfoImage.src}?format=webp&width=423&height=229&fit=crop`
                                            )}
                                            alt={
                                                collectionInfo
                                                    .collectionInfoImage.altText
                                            }
                                        />
                                    </picture>
                                )}
                            </section>
                        </>
                    )
                )}
                {flowboxViewModel && (
                    <>
                        <DividerWide margin="my-10" />
                        <Flowbox
                            text={flowboxViewModel.text}
                            clauseText={flowboxViewModel.clauseText}
                            textContainerMaxWidth={
                                flowboxViewModel.textContainerMaxWidth
                            }
                            flowDiv={flowboxViewModel.flowDiv}
                        />
                    </>
                )}
            </div>
        </>
    );
};

export default ProductDetailPage;
